/** Préférences joueur, conservées dans le navigateur. Réglages de départ : gamedesign §13. */
import type { ShipId } from '../data';
import type { MouseMode } from '../sim/pilots/player';

export type Quality = 'low' | 'medium' | 'high' | 'epic';
/** « throttle » : Z/S règlent un accélérateur qui reste en place ; « hold » : poussée tant que la touche est tenue. */
export type ThrottleMode = 'throttle' | 'hold';

export interface Settings {
  mouseSensitivity: number;
  /** Comme dans Unreal : actif = souris vers le haut, nez vers le haut. */
  invertPitch: boolean;
  mouseMode: MouseMode;
  throttleMode: ThrottleMode;
  quality: Quality;
  hudEnhanced: boolean;
  sound: boolean;
  volume: number;
  reducedFlashes: boolean;
  reducedCameraMotion: boolean;
  /** Envoi des sessions au service de télémétrie (aucune adresse IP conservée). */
  shareTelemetry: boolean;
  /** Nom du pilote, demandé au premier lancement et joint à la télémétrie. */
  playerName: string;
  tutorialDone: boolean;
  lastShip: ShipId;
  /** Version des préférences (migrations). */
  version: number;
}

export const defaultSettings: Settings = {
  mouseSensitivity: 0.16,
  invertPitch: true,
  mouseMode: 'joystick',
  throttleMode: 'throttle',
  quality: 'high',
  hudEnhanced: true,
  sound: true,
  volume: 0.8,
  reducedFlashes: false,
  reducedCameraMotion: false,
  shareTelemetry: true,
  playerName: '',
  tutorialDone: false,
  lastShip: 'taka',
  version: 2,
};

const KEY = 'space-arena.settings.v1';

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...defaultSettings };
    const parsed = JSON.parse(raw) as Partial<Settings>;
    const saved = { ...defaultSettings, ...parsed };
    // v2 : le manche virtuel devient le mode souris par défaut (retour « pas de sensation de voler »).
    if ((parsed.version ?? 1) < 2) {
      saved.mouseMode = 'joystick';
      saved.version = 2;
    }
    return saved;
  } catch {
    return { ...defaultSettings };
  }
}

export function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    // Stockage indisponible (navigation privée, blocage) : les réglages restent valables pour la session.
  }
}

export const SENSITIVITY_MIN = 0.06;
export const SENSITIVITY_MAX = 0.4;
export const SENSITIVITY_STEP = 0.02;
